import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import api from '../../config/api';
import webBannerService from '../../services/webBannerService';
import contentSectionService from '../../services/contentSectionService';
import imageService from '../../services/imageService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import ImageCropper from '../../components/Common/ImageCropper';
import MessageComponent from '../../components/Common/MessageComponent';
import {
  ACTION, ACTIONS, FREQUENCIES, PAGE, PAGES, SLOT, SLOTS, TYPE,
  extractErrors, pageMeta, toLocalInput, toUtcIso,
} from './constants';

const emptyForm = () => ({
  name: '', imageUrl: '', mobileImageUrl: '',
  titleEn: '', titleAr: '', subtitleEn: '', subtitleAr: '',
  textAlign: 0, textTheme: 0,
  style: 0, imageSide: 0, groupTitleEn: '', groupTitleAr: '', groupSubtitleEn: '', groupSubtitleAr: '',
  buttonTextEn: '', buttonTextAr: '',
  actionType: ACTION.NONE, actionTarget: '', openInNewTab: false,
  page: PAGE.STORE, slot: SLOT.TOP, anchorSectionId: '',
  displayType: TYPE.INLINE, popupDelaySeconds: 3, popupFrequency: 1,
  isActive: true, startsAt: '', endsAt: '',
});

const fromDto = (d) => ({
  name: d.name || '', imageUrl: d.imageUrl || '', mobileImageUrl: d.mobileImageUrl || '',
  titleEn: d.titleEn || '', titleAr: d.titleAr || '', subtitleEn: d.subtitleEn || '', subtitleAr: d.subtitleAr || '',
  textAlign: d.textAlign, textTheme: d.textTheme,
  style: d.style || 0, imageSide: d.imageSide || 0,
  groupTitleEn: d.groupTitleEn || '', groupTitleAr: d.groupTitleAr || '', groupSubtitleEn: d.groupSubtitleEn || '', groupSubtitleAr: d.groupSubtitleAr || '',
  buttonTextEn: d.buttonTextEn || '', buttonTextAr: d.buttonTextAr || '',
  actionType: d.actionType, actionTarget: d.actionTarget || '', openInNewTab: d.openInNewTab,
  page: d.page, slot: d.slot, anchorSectionId: d.anchorSectionId || '',
  displayType: d.displayType, popupDelaySeconds: d.popupDelaySeconds, popupFrequency: d.popupFrequency,
  isActive: d.isActive, startsAt: toLocalInput(d.startsAtUtc), endsAt: toLocalInput(d.endsAtUtc),
});

const toRequest = (f) => ({
  name: f.name,
  imageUrl: f.imageUrl,
  mobileImageUrl: f.mobileImageUrl || null,
  titleEn: f.titleEn || null, titleAr: f.titleAr || null, subtitleEn: f.subtitleEn || null, subtitleAr: f.subtitleAr || null,
  textAlign: f.textAlign, textTheme: f.textTheme,
  style: f.displayType === TYPE.POPUP ? 0 : f.style, imageSide: f.imageSide,
  groupTitleEn: f.groupTitleEn || null, groupTitleAr: f.groupTitleAr || null, groupSubtitleEn: f.groupSubtitleEn || null, groupSubtitleAr: f.groupSubtitleAr || null,
  buttonTextEn: f.buttonTextEn || null, buttonTextAr: f.buttonTextAr || null,
  actionType: f.actionType, actionTarget: f.actionType === ACTION.NONE ? null : f.actionTarget || null,
  openInNewTab: f.openInNewTab,
  page: f.page, slot: f.slot, anchorSectionId: f.slot === SLOT.AFTER_SECTION && f.displayType === TYPE.INLINE ? f.anchorSectionId || null : null,
  displayType: f.displayType, popupDelaySeconds: Number(f.popupDelaySeconds) || 0, popupFrequency: f.popupFrequency,
  isActive: f.isActive, startsAtUtc: toUtcIso(f.startsAt), endsAtUtc: toUtcIso(f.endsAt),
});

const SPLIT_ASPECTS = [
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:10', value: 16 / 10 },
];
const TILE_ASPECTS = [
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '4:3', value: 4 / 3 },
];
const STYLE_CARDS = [
  { v: 0, en: 'Image with text on top', ar: 'صورة مع نص فوقها', denEn: 'Full-width banner. Optional text and button over the image.', denAr: 'بانر بعرض الصفحة. نص وزر اختياريان فوق الصورة.' },
  { v: 1, en: 'Image beside text', ar: 'صورة بجانب النص', denEn: 'Image on one side; title, text and a round button on the other.', denAr: 'صورة على جانب، وعنوان ونص وزر مستدير على الجانب الآخر.' },
  { v: 2, en: 'Collection tile', ar: 'بلاطة مجموعة', denEn: 'Image with a caption. Tiles in the same position form a grid under one heading.', denAr: 'صورة مع تسمية. البلاطات في نفس الموضع تُعرض كشبكة تحت عنوان واحد.' },
];

const POPUP_ASPECTS = [
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '3:4', value: 3 / 4 },
  { label: '16:9', value: 16 / 9 },
];

const Group = ({ title, hint, errors, children }) => (
  <section className="card cs-group">
    <div className="cs-group-head">
      <h2>{title}</h2>
      {hint ? <p>{hint}</p> : null}
    </div>
    {errors && errors.length > 0 ? (
      <ul className="cs-errors">{errors.map((m) => <li key={m}>{m}</li>)}</ul>
    ) : null}
    <div className="cs-group-body">{children}</div>
  </section>
);

const ImageField = ({ label, hint, value, onChange, onError, tr, aspects, outWidth }) => {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // file waiting in the crop dialog

  const upload = async (file) => {
    setPending(null);
    try {
      setBusy(true);
      const res = await imageService.uploadImages([file], 'banners');
      const url = res?.imageUrls?.[0];
      if (url) onChange(url);
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const src = getProductImageUrl(value);
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div className="wb-image">{src ? <img src={src} alt="" /> : <span>{tr('No image', 'لا توجد صورة')}</span>}</div>
      <div className="cs-banner-actions">
        <label className="btn btn-secondary btn-small">
          {busy ? tr('Uploading…', 'جارٍ الرفع…') : value ? tr('Replace image', 'استبدال الصورة') : tr('Upload image', 'رفع صورة')}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = ''; // allow choosing the same file again
              if (f) setPending(f);
            }}
          />
        </label>
        {value ? <button type="button" className="btn btn-ghost btn-small" onClick={() => onChange('')}>{tr('Remove', 'إزالة')}</button> : null}
      </div>
      {hint ? <div className="form-hint">{hint}</div> : null}
      {pending ? (
        <ImageCropper
          file={pending}
          aspects={aspects}
          outWidth={outWidth}
          onCancel={() => setPending(null)}
          onDone={upload}
          onUseOriginal={() => upload(pending)}
        />
      ) : null}
    </div>
  );
};

/** Product picker for the "open a product" action. */
const ProductTarget = ({ value, onChange, tr, isRtl }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    let off = false;
    if (!value) { setSelected(null); return undefined; }
    api.get(`/api/products/${value}/details`, { params: { admin: true } })
      .then((r) => { if (!off) setSelected({ id: value, name: isRtl ? r.data.nameAr || r.data.nameEn : r.data.nameEn }); })
      .catch(() => { if (!off) setSelected({ id: value, name: value }); });
    return () => { off = true; };
  }, [value, isRtl]);

  // search as you type (also lists the first products when the box is empty)
  useEffect(() => {
    if (value) return undefined;
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    const t = setTimeout(async () => {
      try {
        const r = await contentSectionService.productLookup({ sectionType: 5, search: q.trim() || null, take: 10 });
        if (mine === seq.current) setResults(Array.isArray(r) ? r : []);
      } catch (e) {
        if (mine === seq.current) {
          setResults([]);
          setError(extractErrors(e, tr('Search failed.', 'فشل البحث.')).messages[0]);
        }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, value]);

  if (selected) {
    return (
      <div className="cs-hint cs-hint--accent">
        {tr('Selected product', 'المنتج المختار')}: <strong>{selected.name}</strong>{' '}
        <button type="button" className="btn btn-ghost btn-small" onClick={() => { onChange(''); setQ(''); }}>{tr('Change', 'تغيير')}</button>
      </div>
    );
  }

  return (
    <div className="wb-product">
      <input className="form-input" type="text" placeholder={tr('Search products by name or brand…', 'ابحث عن منتج بالاسم أو الماركة…')} value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
      {error ? <div className="cs-alert">{error}</div> : null}
      {loading ? <div className="cs-hint">{tr('Searching…', 'جارٍ البحث…')}</div> : null}
      {!loading && !error && results.length === 0 ? (
        <div className="cs-empty">{tr('No products found.', 'لا توجد منتجات مطابقة.')}</div>
      ) : null}
      {results.length > 0 ? (
        <ul className="cs-results">
          {results.map((p) => (
            <li className="cs-result" key={p.productId}>
              {getProductImageUrl(p.imageUrl) ? <img className="cs-thumb" src={getProductImageUrl(p.imageUrl)} alt="" loading="lazy" /> : <span className="cs-thumb" />}
              <div className="cs-pinned-info">
                <div className="cs-pinned-name">{isRtl ? p.nameAr || p.nameEn : p.nameEn}</div>
                <div className="cs-pinned-meta">{isRtl ? p.brandNameAr || p.brandNameEn : p.brandNameEn}</div>
              </div>
              <button type="button" className="btn btn-secondary btn-small" onClick={() => onChange(p.productId)}>{tr('Select', 'اختيار')}</button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const WebBannerEdit = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { isRtl } = useLocalization();
  const tr = (en, ar) => (isRtl ? ar : en);

  const [form, setForm] = useState(emptyForm);
  const [options, setOptions] = useState({ sections: [], brands: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ fields: {}, messages: [] });
  const [toast, setToast] = useState({ visible: false, message: '' });
  const [previewMobile, setPreviewMobile] = useState(false);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const [opts, dto] = await Promise.all([webBannerService.options(), isNew ? null : webBannerService.get(id)]);
        if (off) return;
        setOptions(opts);
        if (dto) setForm(fromDto(dto));
      } catch (e) {
        if (!off) setErrors(extractErrors(e, tr('Failed to load the banner.', 'تعذر تحميل البانر.')));
      } finally {
        if (!off) setLoading(false);
      }
    })();
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setErrors({ fields: {}, messages: [] });
    try {
      const body = toRequest(form);
      const saved = isNew ? await webBannerService.create(body) : await webBannerService.update(id, body);
      setToast({ visible: true, message: tr('Banner saved. The website updates within a minute.', 'تم حفظ البانر. يظهر على الموقع خلال دقيقة.') });
      if (isNew) navigate(`/web-banners/${saved.id}`, { replace: true });
      else setForm(fromDto(saved));
    } catch (err) {
      setErrors(extractErrors(err, tr('Failed to save the banner.', 'تعذر حفظ البانر.')));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading text={tr('Loading banner...', 'جاري تحميل البانر...')} />;

  const f = errors.fields;
  const popup = form.displayType === TYPE.POPUP;
  const overlay = popup || form.style === 0;
  const tile = form.style === 2 && !popup;
  const split = form.style === 1 && !popup;
  const supportsSections = pageMeta(form.page).sections;
  const hasButton = form.buttonTextEn || form.buttonTextAr;
  const imgSrc = getProductImageUrl(previewMobile && form.mobileImageUrl ? form.mobileImageUrl : form.imageUrl);
  const pTitle = isRtl ? form.titleAr || form.titleEn : form.titleEn || form.titleAr;
  const pSub = isRtl ? form.subtitleAr || form.subtitleEn : form.subtitleEn || form.subtitleAr;
  const pBtn = isRtl ? form.buttonTextAr || form.buttonTextEn : form.buttonTextEn || form.buttonTextAr;
  const ALIGN = ['start', 'center', 'end'];

  return (
    <div className="cs-page">
      <MessageComponent message={toast.message} type="success" isVisible={toast.visible} onClose={() => setToast((t) => ({ ...t, visible: false }))} />

      <div className="page-header">
        <div className="page-header-main">
          <Link to="/web-banners" className="cs-back">{isRtl ? '→' : '←'} {tr('Website Banners', 'بانرات الموقع')}</Link>
          <h1 className="page-title">{isNew ? tr('New banner', 'بانر جديد') : form.name || tr('Edit banner', 'تعديل البانر')}</h1>
        </div>
        <div className="page-header-actions">
          <Button variant="secondary" onClick={() => navigate('/web-banners')}>{tr('Cancel', 'إلغاء')}</Button>
          <Button onClick={save} disabled={saving}>{saving ? tr('Saving…', 'جارٍ الحفظ…') : tr('Save banner', 'حفظ البانر')}</Button>
        </div>
      </div>

      {errors.messages.length > 0 ? (
        <div className="cs-alert" role="alert">
          <strong>{tr('Please fix the following:', 'يرجى تصحيح ما يلي:')}</strong>
          <ul>{errors.messages.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      ) : null}

      <form className="cs-layout" onSubmit={save}>
        <div className="cs-main">
          <Group
            title={tr('1 · Look', '١ · الشكل')}
            hint={popup ? tr('Popups always show the image only.', 'النافذة المنبثقة تعرض الصورة فقط.') : tr('Choose how this banner is drawn on the website.', 'اختر كيف يُرسم هذا البانر على الموقع.')}
            errors={f.style}
          >
            <div className="cs-type-grid">
              {STYLE_CARDS.filter((c) => !popup || c.v === 0).map((c) => (
                <button type="button" key={c.v} className={`cs-type ${form.style === c.v || (popup && c.v === 0) ? 'is-active' : ''}`} onClick={() => set({ style: c.v })}>
                  <strong>{isRtl ? c.ar : c.en}</strong>
                  <span>{isRtl ? c.denAr : c.denEn}</span>
                </button>
              ))}
            </div>
            {split ? (
              <div className="form-group">
                <label className="form-label">{tr('Image side', 'جهة الصورة')}</label>
                <div className="cs-limit">
                  {[[0, 'At the start (right in Arabic)', 'في البداية (يمين في العربية)'], [1, 'At the end (left in Arabic)', 'في النهاية (يسار في العربية)']].map(([v, en, ar]) => (
                    <button type="button" key={v} className={`cs-chip ${form.imageSide === v ? 'is-active' : ''}`} onClick={() => set({ imageSide: v })}>{tr(en, ar)}</button>
                  ))}
                </div>
              </div>
            ) : null}
            {tile && !popup ? (
              <div className="cs-hint cs-hint--accent">
                {tr('Create one banner per tile, all with the same screen and position. The grid heading below is taken from the first tile in the list.', 'أنشئ بانراً لكل بلاطة، كلها في نفس الصفحة والموضع. عنوان الشبكة في الأسفل يؤخذ من أول بلاطة في القائمة.')}
              </div>
            ) : null}
          </Group>

          <Group title={tr('2 · Image', '٢ · الصورة')} errors={[...(f.name || []), ...(f.imageUrl || []), ...(f.mobileImageUrl || [])]}>
            <div className="form-group">
              <label className="form-label">{tr('Name (only you see it)', 'الاسم (يظهر لك فقط)')} <span className="text-danger">*</span></label>
              <input className={`form-input ${f.name ? 'error' : ''}`} value={form.name} maxLength={200} onChange={(e) => set({ name: e.target.value })} placeholder={tr('e.g. Eid offers – home banner', 'مثال: عروض العيد – بانر الرئيسية')} />
            </div>
            <ImageField
              label={tr('Banner image', 'صورة البانر')} value={form.imageUrl} onChange={(v) => set({ imageUrl: v })}
              onError={(m) => setErrors({ fields: {}, messages: [m] })} tr={tr}
              aspects={popup ? POPUP_ASPECTS : split ? SPLIT_ASPECTS : tile ? TILE_ASPECTS : [{ label: '16:5', value: 16 / 5 }]}
              outWidth={popup ? 800 : split ? 1000 : tile ? 900 : 1920}
              hint={popup ? tr('Popup: any ratio; recommended width 800px.', 'نافذة منبثقة: أي نسبة؛ العرض الموصى به 800 بكسل.')
                : split ? tr('Recommended 1000 px wide, square (1:1) or 4:3.', 'الموصى به عرض 1000 بكسل، مربعة (1:1) أو 4:3.')
                : tile ? tr('Recommended 900 × 900 px (1:1). Use the same ratio for every tile.', 'الموصى به 900 × 900 بكسل (1:1). استخدم النسبة نفسها لكل البلاطات.')
                : tr('Recommended 1920 × 600 px (16:5). It is cropped to fit.', 'الموصى به 1920 × 600 بكسل (16:5). تُقصّ لتناسب المساحة.')}
            />
            {!tile || popup ? (
            <ImageField
              label={tr('Phone image (optional)', 'صورة الجوال (اختيارية)')} value={form.mobileImageUrl} onChange={(v) => set({ mobileImageUrl: v })}
              onError={(m) => setErrors({ fields: {}, messages: [m] })} tr={tr}
              aspects={[{ label: '4:3', value: 4 / 3 }]} outWidth={800}
              hint={tr('Shown on small screens instead of the main image. Recommended 800 × 600 px (4:3).', 'تظهر على الشاشات الصغيرة بدل الصورة الرئيسية. الموصى به 800 × 600 بكسل (4:3).')}
            />
            ) : null}
          </Group>

          <Group
            title={tile && !popup ? tr('3 · Caption and grid heading', '٣ · التسمية وعنوان الشبكة') : tr('3 · Text (optional)', '٣ · النص (اختياري)')}
            hint={tile && !popup ? tr('The caption sits under the image. The heading appears once above all the tiles.', 'التسمية تحت الصورة. والعنوان يظهر مرة واحدة فوق كل البلاطات.') : tr('Leave empty if the image already contains its text.', 'اتركه فارغاً إذا كانت الصورة تحتوي النص أصلاً.')}
          >
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tile && !popup ? tr('Caption (Arabic)', 'التسمية (عربي)') : tr('Title (Arabic)', 'العنوان (عربي)')}</label>
                <input className="form-input" dir="rtl" value={form.titleAr} maxLength={200} onChange={(e) => set({ titleAr: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{tile && !popup ? tr('Caption (English)', 'التسمية (إنجليزي)') : tr('Title (English)', 'العنوان (إنجليزي)')}</label>
                <input className="form-input" value={form.titleEn} maxLength={200} onChange={(e) => set({ titleEn: e.target.value })} />
              </div>
            </div>
            {!tile || popup ? (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Text (Arabic)', 'النص (عربي)')}</label>
                <textarea className="form-textarea" dir="rtl" rows={2} value={form.subtitleAr} maxLength={500} onChange={(e) => set({ subtitleAr: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Text (English)', 'النص (إنجليزي)')}</label>
                <textarea className="form-textarea" rows={2} value={form.subtitleEn} maxLength={500} onChange={(e) => set({ subtitleEn: e.target.value })} />
              </div>
            </div>
            ) : null}
            {tile && !popup ? (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{tr('Grid heading (Arabic)', 'عنوان الشبكة (عربي)')}</label>
                    <input className="form-input" dir="rtl" value={form.groupTitleAr} maxLength={200} onChange={(e) => set({ groupTitleAr: e.target.value })} placeholder="مجموعات مختارة لها وله" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Grid heading (English)', 'عنوان الشبكة (إنجليزي)')}</label>
                    <input className="form-input" value={form.groupTitleEn} maxLength={200} onChange={(e) => set({ groupTitleEn: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{tr('Grid sub-heading (Arabic)', 'العنوان الفرعي للشبكة (عربي)')}</label>
                    <input className="form-input" dir="rtl" value={form.groupSubtitleAr} maxLength={400} onChange={(e) => set({ groupSubtitleAr: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Grid sub-heading (English)', 'العنوان الفرعي للشبكة (إنجليزي)')}</label>
                    <input className="form-input" value={form.groupSubtitleEn} maxLength={400} onChange={(e) => set({ groupSubtitleEn: e.target.value })} />
                  </div>
                </div>
              </>
            ) : null}
            {overlay ? (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Text position', 'موضع النص')}</label>
                <div className="cs-limit">
                  {[[0, 'Start', 'البداية'], [1, 'Center', 'الوسط'], [2, 'End', 'النهاية']].map(([v, en, ar]) => (
                    <button type="button" key={v} className={`cs-chip ${form.textAlign === v ? 'is-active' : ''}`} onClick={() => set({ textAlign: v })}>{tr(en, ar)}</button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Text color', 'لون النص')}</label>
                <div className="cs-limit">
                  {[[0, 'White (dark image)', 'أبيض (صورة داكنة)'], [1, 'Black (light image)', 'أسود (صورة فاتحة)']].map(([v, en, ar]) => (
                    <button type="button" key={v} className={`cs-chip ${form.textTheme === v ? 'is-active' : ''}`} onClick={() => set({ textTheme: v })}>{tr(en, ar)}</button>
                  ))}
                </div>
              </div>
            </div>
            ) : null}
          </Group>

          <Group
            title={tile && !popup ? tr('4 · When the tile is clicked', '٤ · عند الضغط على البلاطة') : tr('4 · Button and action (optional)', '٤ · الزر وما يحدث عند الضغط (اختياري)')}
            hint={tr('The whole banner is clickable when an action is set. Add button text to also show a button.', 'يصبح البانر كله قابلاً للنقر عند تحديد إجراء. أضف نص زر ليظهر زر أيضاً.')}
            errors={[...(f.actionType || []), ...(f.actionTarget || []), ...(f.buttonText || [])]}
          >
            {!tile || popup ? (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Button text (Arabic)', 'نص الزر (عربي)')}</label>
                <input className="form-input" dir="rtl" value={form.buttonTextAr} maxLength={100} onChange={(e) => set({ buttonTextAr: e.target.value })} placeholder="تسوق الآن" />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Button text (English)', 'نص الزر (إنجليزي)')}</label>
                <input className="form-input" value={form.buttonTextEn} maxLength={100} onChange={(e) => set({ buttonTextEn: e.target.value })} placeholder="Shop now" />
              </div>
            </div>
            ) : null}

            <div className="form-group">
              <label className="form-label">{tr('When clicked', 'عند الضغط')}</label>
              <select className={`form-select ${f.actionType ? 'error' : ''}`} value={form.actionType} onChange={(e) => set({ actionType: Number(e.target.value), actionTarget: '' })}>
                {ACTIONS.map((a) => <option key={a.value} value={a.value}>{isRtl ? a.ar : a.en}</option>)}
              </select>
            </div>

            {form.actionType === ACTION.URL ? (
              <div className="form-group">
                <input className={`form-input ${f.actionTarget ? 'error' : ''}`} dir="ltr" value={form.actionTarget} placeholder="/catalog?sort=price-low  or  https://…" onChange={(e) => set({ actionTarget: e.target.value })} />
                <div className="form-hint">{tr('A path inside the store starts with /, an outside link with https://', 'رابط داخل المتجر يبدأ بـ / ، ورابط خارجي يبدأ بـ https://')}</div>
                <label className="form-checkbox">
                  <input type="checkbox" checked={form.openInNewTab} onChange={(e) => set({ openInNewTab: e.target.checked })} />
                  <span className="form-checkbox-label">{tr('Open in a new tab', 'فتح في تبويب جديد')}</span>
                </label>
              </div>
            ) : null}
            {form.actionType === ACTION.SECTION ? (
              <select className={`form-select ${f.actionTarget ? 'error' : ''}`} value={form.actionTarget} onChange={(e) => set({ actionTarget: e.target.value })}>
                <option value="">{tr('— Select a section —', '— اختر قسماً —')}</option>
                {options.sections.map((s) => <option key={s.id} value={s.id}>{isRtl ? s.titleAr || s.titleEn : s.titleEn}</option>)}
              </select>
            ) : null}
            {form.actionType === ACTION.BRAND ? (
              <select className={`form-select ${f.actionTarget ? 'error' : ''}`} value={form.actionTarget} onChange={(e) => set({ actionTarget: e.target.value })}>
                <option value="">{tr('— Select a brand —', '— اختر ماركة —')}</option>
                {options.brands.map((b) => <option key={b.id} value={b.id}>{isRtl ? b.nameAr || b.nameEn : b.nameEn}</option>)}
              </select>
            ) : null}
            {form.actionType === ACTION.PRODUCT ? (
              <ProductTarget value={form.actionTarget} onChange={(v) => set({ actionTarget: v })} tr={tr} isRtl={isRtl} />
            ) : null}
          </Group>

          <Group
            title={tr('5 · Where it appears', '٥ · مكان الظهور')}
            hint={tr('Several banners in the same screen and position rotate automatically as a carousel.', 'عدة بانرات في نفس الصفحة والموضع تتحول تلقائياً إلى شريط متحرك.')}
            errors={[...(f.page || []), ...(f.slot || []), ...(f.anchorSectionId || []), ...(f.popupDelaySeconds || [])]}
          >
            <div className="form-group">
              <label className="form-label">{tr('Screen', 'الصفحة')}</label>
              <select className="form-select" value={form.page} onChange={(e) => {
                const page = Number(e.target.value);
                set({ page, ...(pageMeta(page).sections ? {} : { slot: form.slot === SLOT.AFTER_SECTION ? SLOT.TOP : form.slot, anchorSectionId: '' }) });
              }}>
                {PAGES.map((p) => <option key={p.value} value={p.value}>{isRtl ? p.ar : p.en}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{tr('How it is shown', 'طريقة العرض')}</label>
              <div className="cs-type-grid">
                <button type="button" className={`cs-type ${!popup ? 'is-active' : ''}`} onClick={() => set({ displayType: TYPE.INLINE })}>
                  <strong>{tr('Banner in the page', 'بانر داخل الصفحة')}</strong>
                  <span>{tr('Part of the page layout', 'جزء من تصميم الصفحة')}</span>
                </button>
                <button type="button" className={`cs-type ${popup ? 'is-active' : ''}`} onClick={() => set({ displayType: TYPE.POPUP })}>
                  <strong>{tr('Popup', 'نافذة منبثقة')}</strong>
                  <span>{tr('Appears when the page opens', 'تظهر عند فتح الصفحة')}</span>
                </button>
              </div>
            </div>

            {!popup ? (
              <>
                <div className="form-group">
                  <label className="form-label">{tr('Position', 'الموضع')}</label>
                  <select className="form-select" value={form.slot} onChange={(e) => set({ slot: Number(e.target.value) })}>
                    {SLOTS.filter((s) => s.value !== SLOT.AFTER_SECTION || supportsSections).map((s) => (
                      <option key={s.value} value={s.value}>{isRtl ? s.ar : s.en}</option>
                    ))}
                  </select>
                </div>
                {form.slot === SLOT.AFTER_SECTION ? (
                  <div className="form-group">
                    <label className="form-label">{tr('Under which section?', 'تحت أي قسم؟')}</label>
                    <select className={`form-select ${f.anchorSectionId ? 'error' : ''}`} value={form.anchorSectionId} onChange={(e) => set({ anchorSectionId: e.target.value })}>
                      <option value="">{tr('— Select a section —', '— اختر قسماً —')}</option>
                      {options.sections.map((s) => <option key={s.id} value={s.id}>{isRtl ? s.titleAr || s.titleEn : s.titleEn}</option>)}
                    </select>
                    <div className="form-hint">{tr('If that section is disabled or empty, the banner is hidden too.', 'إذا تعطّل ذلك القسم أو كان فارغاً يختفي البانر أيضاً.')}</div>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{tr('Show after (seconds)', 'يظهر بعد (ثوانٍ)')}</label>
                  <input className={`form-input ${f.popupDelaySeconds ? 'error' : ''}`} type="number" min="0" max="120" value={form.popupDelaySeconds} onChange={(e) => set({ popupDelaySeconds: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{tr('How often', 'عدد مرات الظهور')}</label>
                  <select className="form-select" value={form.popupFrequency} onChange={(e) => set({ popupFrequency: Number(e.target.value) })}>
                    {FREQUENCIES.map((x) => <option key={x.value} value={x.value}>{isRtl ? x.ar : x.en}</option>)}
                  </select>
                </div>
              </div>
            )}
          </Group>

          <Group title={tr('6 · Schedule', '٦ · الجدولة')} hint={tr('Leave empty for always. Times are in your timezone.', 'اتركها فارغة ليكون دائماً ظاهراً. الأوقات بتوقيتك المحلي.')} errors={f.endsAtUtc}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Starts', 'يبدأ')}</label>
                <input className="form-input" type="datetime-local" value={form.startsAt} onChange={(e) => set({ startsAt: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Ends', 'ينتهي')}</label>
                <input className={`form-input ${f.endsAtUtc ? 'error' : ''}`} type="datetime-local" value={form.endsAt} onChange={(e) => set({ endsAt: e.target.value })} />
              </div>
            </div>
            <label className="form-checkbox">
              <input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
              <span className="form-checkbox-label">{tr('Active', 'مفعّل')}</span>
            </label>
          </Group>
        </div>

        <aside className="cs-side">
          <div className="card cs-preview-card">
            <div className="cs-preview-head">
              <h2>{tr('Preview', 'معاينة')}</h2>
              {form.mobileImageUrl ? (
                <button type="button" className="btn btn-ghost btn-small" onClick={() => setPreviewMobile((v) => !v)}>
                  {previewMobile ? tr('Desktop', 'سطح المكتب') : tr('Phone', 'الجوال')}
                </button>
              ) : null}
            </div>
            {split ? (
              <div className={`wb-split ${form.imageSide === 1 ? 'is-end' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
                <div className="wb-split-img">{imgSrc ? <img src={imgSrc} alt="" /> : <span>—</span>}</div>
                <div className="wb-split-txt">
                  {pTitle ? <strong>{pTitle}</strong> : null}
                  {pSub ? <p>{pSub}</p> : null}
                  {hasButton ? <span className="wb-pill">{pBtn} {isRtl ? '←' : '→'}</span> : null}
                </div>
              </div>
            ) : null}
            {tile ? (
              <div className="wb-tile" dir={isRtl ? 'rtl' : 'ltr'}>
                {(isRtl ? form.groupTitleAr || form.groupTitleEn : form.groupTitleEn || form.groupTitleAr) ? (
                  <div className="wb-tile-head">{isRtl ? form.groupTitleAr || form.groupTitleEn : form.groupTitleEn || form.groupTitleAr}</div>
                ) : null}
                <div className="wb-tile-img">{imgSrc ? <img src={imgSrc} alt="" /> : <span>—</span>}</div>
                {pTitle ? <div className="wb-tile-cap">{pTitle}</div> : null}
              </div>
            ) : null}
            {overlay ? (
            <div className={`wb-preview ${previewMobile ? 'wb-preview--m' : ''} ${popup ? 'wb-preview--popup' : ''}`}>
              {imgSrc ? <img src={imgSrc} alt="" /> : <span className="cs-empty">{tr('Upload an image to preview', 'ارفع صورة للمعاينة')}</span>}
              {imgSrc && (pTitle || pSub || pBtn) ? (
                <div className={`wb-text wb-text--${ALIGN[form.textAlign]} wb-theme--${form.textTheme === 1 ? 'dark' : 'light'}`}>
                  {pTitle ? <strong>{pTitle}</strong> : null}
                  {pSub ? <p>{pSub}</p> : null}
                  {hasButton ? <span className="wb-btn">{pBtn}</span> : null}
                </div>
              ) : null}
            </div>
            ) : null}
            <p className="cs-hint">{tr('Approximate preview. The website crops the image to fit its space.', 'معاينة تقريبية. الموقع يقصّ الصورة لتناسب مساحتها.')}</p>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default WebBannerEdit;
